import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, FormControl, FormLabel, Input, Select, Textarea,
  SimpleGrid, VStack, Text, Badge, HStack, useToast, Table, Thead, Tbody,
  Tr, Th, Td, Divider, Box,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import type { AudienciaJuzgado, CitatorioAudiencia, EntregaCitatorio } from './juzgadoTypes';
import juzgadoApi from '../../services/juzgadoApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  audiencia: AudienciaJuzgado | null;
}

const EMPTY_CIT: Partial<CitatorioAudiencia> = { nombreCitado: '', motivo: '', horaComparecencia: '' };
const EMPTY_ENT: Partial<EntregaCitatorio> = { entregadoPor: 'Funcionario', metodo: 'Físico', estatusCitacion: 'Entregado' };

export default function ModalCitatorio({ isOpen, onClose, audiencia }: Props) {
  const [citatorios, setCitatorios] = useState<CitatorioAudiencia[]>([]);
  const [formCit, setFormCit] = useState<Partial<CitatorioAudiencia>>(EMPTY_CIT);
  const [crearCit, setCrearCit] = useState(false);
  const [entregaId, setEntregaId] = useState<number | null>(null);
  const [formEnt, setFormEnt] = useState<Partial<EntregaCitatorio>>(EMPTY_ENT);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const setCit = (k: keyof CitatorioAudiencia, v: any) => setFormCit(p => ({ ...p, [k]: v }));
  const setEnt = (k: keyof EntregaCitatorio, v: any) => setFormEnt(p => ({ ...p, [k]: v }));

  const cargar = async () => {
    if (!audiencia?.id) return;
    const data = await juzgadoApi.listarCitatorios(audiencia.id);
    setCitatorios(data);
  };

  useEffect(() => {
    if (isOpen) {
      cargar();
      setFormCit({
        ...EMPTY_CIT,
        nombreCitado: audiencia?.imputado ?? '',
        fechaComparecencia: audiencia?.fechaProgramada ? String(audiencia.fechaProgramada).substring(0, 10) : '',
        horaComparecencia: audiencia?.horaInicio ?? '',
        lugarAudiencia: audiencia?.lugarSala ?? 'Juzgado Municipal',
        nombreJuez: audiencia?.juezAsignado ?? '',
        motivo: audiencia?.tipoAudiencia ?? '',
      });
    }
  }, [isOpen, audiencia]);

  const handleCrearCitatorio = async () => {
    if (!audiencia?.id) return;
    if (!formCit.nombreCitado || !formCit.fechaComparecencia) {
      return toast({ title: 'Nombre y fecha son requeridos', status: 'warning' });
    }
    setSaving(true);
    try {
      await juzgadoApi.crearCitatorio(audiencia.id, formCit);
      toast({ title: 'Citatorio generado', status: 'success' });
      setCrearCit(false);
      await cargar();
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleRegistrarEntrega = async () => {
    if (!entregaId) return;
    if (!formEnt.fechaEntrega) return toast({ title: 'Fecha de entrega requerida', status: 'warning' });
    setSaving(true);
    try {
      await juzgadoApi.registrarEntregaCitatorio(entregaId, formEnt);
      toast({ title: 'Entrega registrada', status: 'success' });
      setEntregaId(null);
      await cargar();
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const colorEstatus = (e?: string) => {
    const s = (e || '').toLowerCase();
    if (s === 'entregado') return 'green';
    if (s === 'rechazado') return 'red';
    if (s === 'no localizado') return 'orange';
    return 'gray';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg="#5b182b" color="white" borderTopRadius="md">
          Citatorios — {audiencia?.folioAudiencia}
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody>
          <HStack justify="flex-end" mb={3}>
            <Button size="sm" bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={() => setCrearCit(!crearCit)}>
              {crearCit ? 'Cancelar' : 'Nuevo Citatorio'}
            </Button>
          </HStack>

          {crearCit && (
            <VStack p={4} border="1px solid" borderColor="#bb8c52" borderRadius="md" mb={4} spacing={3} align="stretch">
              <Text fontWeight="semibold" color="#5b182b">Datos del citatorio</Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Persona citada</FormLabel>
                  <Input value={formCit.nombreCitado ?? ''} onChange={e => setCit('nombreCitado', e.target.value)} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Motivo de la audiencia</FormLabel>
                  <Input value={formCit.motivo ?? ''} onChange={e => setCit('motivo', e.target.value)} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Fecha de comparecencia</FormLabel>
                  <Input type="date" value={formCit.fechaComparecencia ? String(formCit.fechaComparecencia).substring(0, 10) : ''} onChange={e => setCit('fechaComparecencia', e.target.value)} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Hora de comparecencia</FormLabel>
                  <Input type="time" value={formCit.horaComparecencia ?? ''} onChange={e => setCit('horaComparecencia', e.target.value)} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Lugar de audiencia</FormLabel>
                  <Input value={formCit.lugarAudiencia ?? ''} onChange={e => setCit('lugarAudiencia', e.target.value)} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Nombre del juez</FormLabel>
                  <Input value={formCit.nombreJuez ?? ''} onChange={e => setCit('nombreJuez', e.target.value)} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                </FormControl>
              </SimpleGrid>
              <HStack justify="flex-end">
                <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} size="sm" isLoading={saving} onClick={handleCrearCitatorio}>
                  Generar Citatorio
                </Button>
              </HStack>
            </VStack>
          )}

          {entregaId && (
            <VStack p={4} border="1px solid" borderColor="orange.300" borderRadius="md" mb={4} spacing={2} align="stretch">
              <Text fontWeight="semibold" color="orange.600">Registrar entrega del citatorio #{entregaId}</Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2}>
                <FormControl>
                  <FormLabel fontSize="sm">Entregado por</FormLabel>
                  <Select value={formEnt.entregadoPor ?? ''} onChange={e => setEnt('entregadoPor', e.target.value)} borderColor="#bb8c52">
                    {['Cartero','Funcionario','Policía','Otro'].map(v => <option key={v} value={v}>{v}</option>)}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Método</FormLabel>
                  <Select value={formEnt.metodo ?? ''} onChange={e => setEnt('metodo', e.target.value)} borderColor="#bb8c52">
                    <option value="Físico">Físico</option>
                    <option value="Digital">Digital</option>
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Fecha de entrega</FormLabel>
                  <Input type="date" value={formEnt.fechaEntrega ?? ''} onChange={e => setEnt('fechaEntrega', e.target.value)} borderColor="#bb8c52" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Estatus de citación</FormLabel>
                  <Select value={formEnt.estatusCitacion ?? ''} onChange={e => setEnt('estatusCitacion', e.target.value)} borderColor="#bb8c52">
                    {['No entregado','Entregado','Rechazado','No localizado','Pendiente','Reprogramado'].map(s => <option key={s} value={s}>{s}</option>)}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Observaciones</FormLabel>
                  <Input value={formEnt.observaciones ?? ''} onChange={e => setEnt('observaciones', e.target.value)} borderColor="#bb8c52" />
                </FormControl>
              </SimpleGrid>
              <HStack>
                <Button size="sm" colorScheme="orange" isLoading={saving} onClick={handleRegistrarEntrega}>Registrar Entrega</Button>
                <Button size="sm" variant="ghost" onClick={() => setEntregaId(null)}>Cancelar</Button>
              </HStack>
            </VStack>
          )}

          <Table size="sm">
            <Thead bg="gray.100">
              <Tr>
                <Th>Folio</Th>
                <Th>Citado</Th>
                <Th>Fecha</Th>
                <Th>Hora</Th>
                <Th>Lugar</Th>
                <Th>Estatus</Th>
                <Th>Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {citatorios.length === 0 && (
                <Tr><Td colSpan={7} textAlign="center" color="gray.400">Sin citatorios</Td></Tr>
              )}
              {citatorios.map(c => (
                <Tr key={c.id}>
                  <Td fontWeight="medium" color="#5b182b">{c.folioCitacion}</Td>
                  <Td>{c.nombreCitado}</Td>
                  <Td>{new Date(c.fechaComparecencia).toLocaleDateString('es-MX')}</Td>
                  <Td>{c.horaComparecencia}</Td>
                  <Td>{c.lugarAudiencia}</Td>
                  <Td><Badge colorScheme={colorEstatus(c.estatus)}>{c.estatus}</Badge></Td>
                  <Td>
                    <Button size="xs" colorScheme="orange" variant="outline"
                      onClick={() => { setEntregaId(c.id); setFormEnt({ ...EMPTY_ENT, fechaEntrega: new Date().toISOString().substring(0, 10) }); }}>
                      Entrega
                    </Button>
                    <Button size="xs" colorScheme="red" variant="outline" as="a"
                      href={juzgadoApi.pdfCitatorio(c.id)} target="_blank">
                      PDF
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Cerrar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
